import { Call, Device } from '@twilio/voice-sdk';

import type { CallQualitySample } from './callQualityTracker';
import type {
  SoftphoneCallHandlers,
  SoftphoneCallQuality,
  SoftphoneClient,
  SoftphoneDiagnostic,
  SoftphoneDialParams,
  SoftphoneMediaStrategy,
  SoftphoneQualityDiagnostic,
  SoftphoneTerminationDiagnostic,
} from './softphoneClient.types';
import { CallQualityTracker } from './callQualityTracker';
import { isRaisedQualityEvent } from './softphoneDiagnosticReport';
import { isRecord, softphoneErrorInfo, softphoneErrorMessage } from './softphoneErrorInfo';

/** Twilio `UserMediaErrors`: the browser refused the mic prompt / could not open the device. */
const MIC_PERMISSION_DENIED = 31401;
const MIC_ACQUISITION_FAILED = 31402;

/**
 * Describe how a call ended, so an instant hangup is diagnosable instead of vanishing into a
 * benign "Call ended". A call that ends before it was ever answered (`becameActive === false`) is
 * the instant-hangup signature — that, an `error` event, or any Twilio `code` marks the
 * termination `abnormal`; a disconnect after the call was active is a normal hangup.
 *
 * Pure: the caller emits it (console + the surface's `onDiagnostic` sink), so there is exactly one
 * place that decides where diagnostics go.
 */
function describeCallTermination(
  event: SoftphoneTerminationDiagnostic['event'],
  voiceCallId: string | null,
  becameActive: boolean,
  quality: SoftphoneCallQuality | null,
  e?: unknown,
): SoftphoneTerminationDiagnostic {
  const { code, description, explanation, message } = softphoneErrorInfo(e);
  return {
    kind: 'termination',
    event,
    voiceCallId,
    becameActive,
    abnormal: event === 'error' || event === 'device-error' || code != null || !becameActive,
    quality,
    ...(code != null ? { code } : {}),
    ...(description != null ? { description } : {}),
    ...(explanation != null ? { explanation } : {}),
    ...(message != null ? { message } : {}),
  };
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

/**
 * The payload the SDK attaches to a `warning` event (`RTCWarning`): which threshold tripped and
 * the sample values that tripped it. Reduced to the two fields worth keeping on a timeline.
 */
function warningDetail(data: unknown): SoftphoneQualityDiagnostic['detail'] {
  if (!isRecord(data)) return null;
  const threshold =
    isRecord(data.threshold) && typeof data.threshold.name === 'string' && typeof data.threshold.value === 'number'
      ? { name: data.threshold.name, value: data.threshold.value }
      : undefined;
  const rawValues = Array.isArray(data.values)
    ? data.values
    : typeof data.value === 'number'
      ? [data.value]
      : undefined;
  const values = rawValues?.filter((v): v is number => typeof v === 'number').map((v) => Math.round(v * 100) / 100);
  if (threshold == null && values == null) return null;
  return { ...(threshold ? { threshold } : {}), ...(values ? { values } : {}) };
}

/** The per-second `RTCSample` the SDK emits, narrowed to what the quality roll-up needs. */
function toQualitySample(sample: unknown): CallQualitySample | null {
  if (!isRecord(sample)) return null;
  return {
    packetsLostFraction: numberOrNull(sample.packetsLostFraction),
    jitter: numberOrNull(sample.jitter),
    rtt: numberOrNull(sample.rtt),
    mos: numberOrNull(sample.mos),
    codecName: typeof sample.codecName === 'string' ? sample.codecName : null,
    audioInputLevel: numberOrNull(sample.audioInputLevel),
    audioOutputLevel: numberOrNull(sample.audioOutputLevel),
  };
}

/** The `deviceChange` payload: the `MediaDeviceInfo`s the call was using that just disappeared. */
function lostDeviceLabels(lostActiveDevices: unknown): string[] {
  if (!Array.isArray(lostActiveDevices)) return [];
  return lostActiveDevices.map((d: unknown) =>
    isRecord(d) && typeof d.label === 'string' && d.label.length > 0 ? d.label : 'unknown device',
  );
}

/**
 * Twilio implementation of the provider-agnostic softphone contract. Outbound calls go
 * through the TwiML App the AccessToken points at: `device.connect({ params })` sends our
 * `voiceCallId` to the server's TwiML endpoint, which resolves the real destination from
 * the voice_call row (the browser never names the number it dials).
 *
 * NOTE: the Twilio Device manages its own mic + audio playback, so the injectable
 * `SoftphoneMediaStrategy` (used by the extension's offscreen mic with Telnyx) is accepted
 * for interface parity but not yet honored here — extension + Twilio needs live validation.
 */
export class TwilioSoftphoneClient implements SoftphoneClient {
  private device: Device | null = null;
  private call: Call | null = null;
  private handlers: SoftphoneCallHandlers | null = null;
  /** Removes exactly the listeners we attached to the current call (set in `dial`). */
  private detachCallListeners: (() => void) | null = null;
  /**
   * The mic MediaStream the SDK acquired for the active call, captured via the public
   * `Call.getLocalStream()` so we can stop its tracks on call-end and clear the browser's
   * "recording" indicator — even if the SDK's own peer-connection teardown doesn't. Nulled
   * whenever the call is torn down; the persistent Device (see #2089) stays registered.
   */
  private localStream: MediaStream | null = null;
  private readonly mediaStrategy?: SoftphoneMediaStrategy;

  constructor(mediaStrategy?: SoftphoneMediaStrategy) {
    this.mediaStrategy = mediaStrategy;
  }

  on(handlers: SoftphoneCallHandlers): void {
    this.handlers = handlers;
  }

  /**
   * The one place call diagnostics leave this client: the console (unchanged, for live debugging)
   * plus the surface's optional sink. Before the sink existed these codes reached the console and
   * nothing else, which is why a five-day production calling outage produced no server-side signal.
   */
  private emitDiagnostic(diagnostic: SoftphoneDiagnostic): void {
    if (diagnostic.kind === 'quality') {
      if (isRaisedQualityEvent(diagnostic)) {
        console.warn('[softphone] call quality', diagnostic);
      } else {
        console.info('[softphone] call quality', diagnostic);
      }
    } else if (diagnostic.kind === 'start-failure') {
      console.error('[softphone] call could not start', diagnostic);
    } else if (diagnostic.kind === 'termination' && diagnostic.abnormal) {
      console.error('[softphone] call terminated abnormally', diagnostic);
    } else if (diagnostic.kind === 'termination' && diagnostic.quality?.degraded) {
      console.warn('[softphone] call ended degraded', diagnostic);
    } else {
      console.info('[softphone] call ended', diagnostic);
    }
    this.handlers?.onDiagnostic?.(diagnostic);
  }

  async init(token: string): Promise<void> {
    const existing = this.device;
    // Reuse the Device only while it's still REGISTERED. Destroying + re-registering the same
    // identity on every dial is what corrupts the 2nd/3rd consecutive call, so we keep the live
    // signaling connection and just refresh the (freshly minted) token. But a device that lost
    // its registration — e.g. superseded by another Device on the same `${userId}:${teamId}`
    // identity, or a network blip dropped it to Unregistered — would fail on `connect()`, so in
    // any non-Registered state we rebuild from scratch instead of reusing a dead connection.
    if (existing && existing.state === Device.State.Registered) {
      if (existing.token !== token) existing.updateToken(token);
      return;
    }
    // Non-reusable: release a dead-but-not-destroyed device before building a fresh one so we
    // don't leak its signaling resources (the original "destroy + recreate" leak in reverse).
    if (existing && existing.state !== Device.State.Destroyed) {
      try {
        existing.destroy();
      } catch {
        // already torn down
      }
    }
    const device = new Device(token, { logLevel: 'error' });
    device.on(Device.EventName.Error, (e: unknown) => {
      // Device-level errors (e.g. 31000 TwiML fetch failure, 31005 transport) are the ones that
      // silently kill an outbound call — report the structured code before surfacing the message.
      this.emitDiagnostic(describeCallTermination('device-error', null, false, null, e));
      this.handlers?.onError(softphoneErrorMessage(e, 'Voice connection error'));
    });
    // The token lasts an hour and the Device stays registered across calls (#2089), so without
    // this a dialer left open — or a call that outlives its token — loses its signaling
    // connection at the hour mark. Twilio's documented remedy: swap in a fresh token here.
    device.on(Device.EventName.TokenWillExpire, () => {
      const refresh = this.handlers?.onTokenWillExpire;
      if (!refresh) return;
      void refresh()
        .then((fresh) => {
          if (this.device === device) device.updateToken(fresh);
        })
        .catch((e: unknown) => {
          // Not fatal here: the Device unregisters when the token lapses and the next `init`
          // rebuilds it; a call in flight reports its own termination.
          console.warn('[softphone] token refresh failed', e);
        });
    });
    this.device = device;
    // Registering isn't strictly required for outbound, but it surfaces token/connection
    // problems up front instead of on the first dial.
    await device.register();
  }

  async dial(params: SoftphoneDialParams): Promise<void> {
    const device = this.device;
    if (!device) throw new Error('Softphone is not initialized');

    // A half-open prior call must not bleed its events into this one.
    this.teardownCall();

    this.handlers?.onStatus('connecting');
    // Only the correlation id is sent; the destination is resolved server-side from the row.
    const call = await device.connect({ params: { voiceCallId: params.voiceCallId } });
    this.call = call;
    // Grab the mic stream the SDK acquired so we can stop it on hangup (best-effort here; the
    // stream may not be negotiated until `accept`, so we refresh it in `onAccept`).
    this.captureLocalStream();

    // Whether this call was ever answered — a terminal event that arrives before this flips true
    // is an instant hangup (the reported bug), which `emitDiagnostic` reports at error level.
    let becameActive = false;
    // The call's media quality, fed by the SDK's warning / reconnect / sample events below and
    // rolled up onto the termination diagnostic — the only record of "the audio cut out".
    const quality = new CallQualityTracker();
    const startedAt = Date.now();
    const secondsIntoCall = () => Math.round((Date.now() - startedAt) / 1000);
    const summary = () => quality.summary(device.edge ?? null);
    const onRinging = () => this.handlers?.onStatus('ringing');
    const onAccept = () => {
      becameActive = true;
      this.captureLocalStream();
      quality.setInputDevice(this.localStream?.getAudioTracks()[0]?.label ?? null);
      this.handlers?.onStatus('active');
    };
    // disconnect/cancel/reject are all terminal; keep them distinct so the log names the exact
    // event (and so detach removes precisely what was attached).
    const makeTerminal = (event: 'disconnect' | 'cancel' | 'reject') => (arg?: unknown) => {
      this.emitDiagnostic(describeCallTermination(event, params.voiceCallId, becameActive, summary(), arg));
      this.handlers?.onStatus('ended');
      this.teardownCall();
      void this.releaseInputDevice();
    };
    const onDisconnect = makeTerminal('disconnect');
    const onCancel = makeTerminal('cancel');
    const onReject = makeTerminal('reject');
    const onError = (e: unknown) => {
      const info = softphoneErrorInfo(e);
      // `device.connect()` resolves BEFORE the SDK asks for the microphone (it runs inside
      // `accept`), so a denied or missing mic doesn't reject `dial` — it comes back here as a
      // UserMediaError on a call that never became active. Report it as the start failure it
      // is (stage `dial`), not as a call that terminated.
      if (!becameActive && (info.code === MIC_PERMISSION_DENIED || info.code === MIC_ACQUISITION_FAILED)) {
        this.emitDiagnostic({
          kind: 'start-failure',
          stage: 'dial',
          voiceCallId: params.voiceCallId,
          message: info.description ?? info.explanation ?? info.message ?? 'Microphone unavailable',
          errorName: info.name ?? null,
          code: info.code,
        });
      } else {
        this.emitDiagnostic(describeCallTermination('error', params.voiceCallId, becameActive, summary(), e));
      }
      this.handlers?.onError(softphoneErrorMessage(e, 'Call error'));
      this.teardownCall();
      void this.releaseInputDevice();
    };
    const emitQuality = (
      event: SoftphoneQualityDiagnostic['event'],
      name: string | null,
      detail: SoftphoneQualityDiagnostic['detail'],
    ) => {
      this.emitDiagnostic({
        kind: 'quality',
        event,
        voiceCallId: params.voiceCallId,
        name,
        detail,
        secondsIntoCall: secondsIntoCall(),
      });
    };
    // The SDK's own network-quality + audio-level monitor: `high-packet-loss`, `high-jitter`,
    // `high-rtt`, `low-mos`, `ice-connectivity-lost`, and `constant-audio-input-level` (the rep's
    // mic went flat — suppressed by the SDK while muted). Each is also published to Twilio
    // Voice Insights by the SDK, but Insights is off for our account, so this is the record.
    const onWarning = (name: unknown, data?: unknown) => {
      if (typeof name !== 'string') return;
      quality.onWarning(name);
      emitQuality('warning', name, warningDetail(data));
    };
    const onWarningCleared = (name: unknown) => {
      if (typeof name !== 'string') return;
      quality.onWarningCleared(name);
      emitQuality('warning-cleared', name, null);
    };
    // Media (ICE) or signaling dropped and the SDK is renegotiating: both parties hear silence
    // until `reconnected`, which also restores the active status.
    const onReconnecting = () => {
      quality.onReconnecting();
      emitQuality('reconnecting', null, null);
    };
    // Media / signaling are back. Only a call that was answered returns to `active`: a signaling
    // blip while still ringing must not mark the call accepted, or a rejected <Dial> afterwards
    // would read as a normal hangup.
    const onReconnected = () => {
      quality.onReconnected();
      emitQuality('reconnected', null, null);
      if (becameActive) onAccept();
    };
    // Once per second while media flows: loss / jitter / rtt / MOS / codec / levels for the
    // roll-up. Muted is passed so a deliberately silent mic doesn't read as a dead one.
    const onSample = (sample: unknown) => {
      const parsed = toQualitySample(sample);
      if (parsed) quality.onSample(parsed, { muted: call.isMuted() });
    };
    // The browser's audio devices changed. The payload names the devices the call was USING that
    // vanished — a Bluetooth headset dropping — after which the SDK falls back to the default
    // mic. Lives on the Device's AudioHelper, not the Call, so it's attached and detached here.
    const onDeviceChange = (lostActiveDevices: unknown) => {
      const lostDevices = lostDeviceLabels(lostActiveDevices);
      quality.onDeviceChange(lostDevices);
      emitQuality('device-change', null, { lostDevices });
      // The track the call now uses may be a different mic; re-read its label.
      this.captureLocalStream();
      quality.setInputDevice(this.localStream?.getAudioTracks()[0]?.label ?? null);
    };

    call.on('ringing', onRinging);
    call.on('accept', onAccept);
    call.on('reconnecting', onReconnecting);
    call.on('reconnected', onReconnected);
    call.on('warning', onWarning);
    call.on('warning-cleared', onWarningCleared);
    call.on('sample', onSample);
    device.audio?.on('deviceChange', onDeviceChange);
    call.on('disconnect', onDisconnect);
    call.on('cancel', onCancel);
    call.on('reject', onReject);
    call.on('error', onError);

    // Detach exactly what we attached (not `removeAllListeners`, which would also strip the
    // SDK's internal listeners needed for clean signaling teardown).
    this.detachCallListeners = () => {
      call.off('ringing', onRinging);
      call.off('accept', onAccept);
      call.off('reconnecting', onReconnecting);
      call.off('reconnected', onReconnected);
      call.off('warning', onWarning);
      call.off('warning-cleared', onWarningCleared);
      call.off('sample', onSample);
      device.audio?.off('deviceChange', onDeviceChange);
      call.off('disconnect', onDisconnect);
      call.off('cancel', onCancel);
      call.off('reject', onReject);
      call.off('error', onError);
    };
  }

  hangup(): void {
    this.call?.disconnect();
  }

  setMuted(muted: boolean): void {
    this.call?.mute(muted);
  }

  sendDigits(digits: string): void {
    this.call?.sendDigits(digits);
  }

  closeCall(): void {
    this.teardownCall();
    void this.releaseInputDevice();
  }

  async destroy(): Promise<void> {
    this.teardownCall();
    try {
      this.device?.destroy();
    } catch {
      // already destroyed
    }
    this.device = null;
    return Promise.resolve();
  }

  /** Capture the SDK's local mic stream for the active call so we can stop it on teardown. */
  private captureLocalStream(): void {
    const stream = this.call?.getLocalStream();
    if (stream) this.localStream = stream;
  }

  /**
   * Stop the mic tracks the SDK acquired for the call so the browser's "recording" indicator
   * clears. Mirrors the codebase's canonical release pattern (VoiceRecorder / offscreen mic).
   * Safe on the pre-dial cleanup path: the next `device.connect()` acquires a fresh stream.
   */
  private stopLocalStream(): void {
    const stream = this.localStream;
    if (!stream) return;
    this.localStream = null;
    for (const track of stream.getTracks()) {
      try {
        track.stop();
      } catch {
        // already stopped
      }
    }
  }

  /**
   * Release the mic held by the Device's AudioHelper WITHOUT destroying the Device, so the
   * recording indicator turns off between calls while the registered Device stays reusable
   * (preserving the consecutive-call fix in #2089). Twilio's documented remedy for the red
   * "recording" symbol; a no-op when no input device is held. Only call once the call is fully
   * torn down — never right before a fresh `dial()` — so it can't race a new connect.
   */
  private async releaseInputDevice(): Promise<void> {
    try {
      await this.device?.audio?.unsetInputDevice();
    } catch {
      // nothing held, or the SDK refused mid-connection
    }
  }

  /** Disconnect + fully detach the active call, leaving the Device registered for reuse. */
  private teardownCall(): void {
    // Stop the mic tracks regardless of call state so the indicator clears even on odd
    // termination paths (instant hangup / error) where the SDK's own close may not run.
    this.stopLocalStream();
    const call = this.call;
    if (!call) return;
    // Detach first so the idempotent `disconnect()` can't re-enter our `onEnded` handler.
    this.detachCallListeners?.();
    this.detachCallListeners = null;
    try {
      call.disconnect();
    } catch {
      // already gone
    }
    this.call = null;
  }
}
